import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import {
  deriveBtcAddresses,
  deriveEvmAddresses,
  deriveSolanaAddresses,
  deriveTronAddresses,
  deriveMoneroAddresses,
  deriveTonAddresses,
  deriveZcashAddresses,
  deriveDogeAddresses,
  deriveXrpAddresses,
  evmNetworkFor,
} from '@incpt/blockchain';
import { SUPPORTED_CHAINS, type Chain } from '@incpt/shared';
import { csGet, csSet, csRemove } from './cloud-storage';
import { api } from './api';
import { generateKeyB64, encryptSeed, decryptSeed, isEncrypted } from './crypto';
import { storeSeedKey, getSeedKey } from './me';
import { getWebUserId } from './sessionUser';
import { readWebSession } from './webSession';
import { seedReadReason } from './seedReadReason';

const SEED_KEY = 'incpt_seed_v1';

// telegram-web-app.js is loaded on the web too and exposes a CloudStorage stub
// that throws WebAppMethodUnsupported outside Telegram. Only the real client
// (non-empty initData) can persist the seed there.
const hasCloud = () =>
  !!window.Telegram?.WebApp?.initData && !!window.Telegram?.WebApp?.CloudStorage;
const tgUserId = () => window.Telegram?.WebApp?.initDataUnsafe?.user?.id ?? null;

/** localStorage key scoped to Telegram user or web server user id. */
const localSeedKey = () => {
  const tg = tgUserId();
  if (tg != null) return `${SEED_KEY}:${tg}`;
  const web = getWebUserId();
  if (web) return `${SEED_KEY}:web:${web}`;
  // Pre-me web boot: temporary slot until setWebUserId runs; migrated after.
  if (readWebSession()) return `${SEED_KEY}:web:pending`;
  return SEED_KEY;
};

function localGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function localSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function localRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** The user's wallet from the backend, or null if not created yet. */
export interface RemoteWallet {
  /** `Chain`, not a copy of it. Spelled out, this described a response the
   *  server already sends for nine chains while claiming three. */
  addresses: Array<{ chain: Chain; eoaAddress: string; derivationIndex?: number }>;
}

/** Synchronous check used to decide the initial UI phase (no onboarding flash). */
export function hasLocalSeed(): boolean {
  return !!localGet(localSeedKey());
}

/** Verify that an unscoped legacy localStorage value derives the current user's
 *  server wallet before assigning it to this Telegram account. */
async function legacyBelongsToCurrentUser(
  initData: string,
  stored: string,
  reauthToken?: string,
): Promise<boolean> {
  let mnemonic = stored;
  if (isEncrypted(stored)) {
    const { key } = await getSeedKey(initData, reauthToken);
    mnemonic = await decryptSeed(stored, key);
  }
  if (!validateMnemonic(mnemonic, wordlist)) return false;

  const remote = await fetchRemoteWallet(initData);
  if (!remote) return false;
  return seedMatchesWallet(mnemonic, remote);
}

/** Read the raw stored value (ciphertext, or legacy plaintext). Telegram
 *  CloudStorage is authoritative and already account-scoped. localStorage is a
 *  fast/offline mirror scoped by Telegram user id.
 *
 *  The old unscoped local key is never trusted blindly: it is claimed only after
 *  its derived addresses match the current user's server wallet. */
export async function loadStoredSeed(
  initData?: string,
  reauthToken?: string,
): Promise<string | null> {
  const scopedKey = localSeedKey();
  if (hasCloud()) {
    const v = await csGet(SEED_KEY).catch(() => null);
    if (v) {
      localSet(scopedKey, v);
      // Remove the old global mirror only when CloudStorage proves it is the
      // current account's exact value. Never delete another account's unknown
      // legacy fallback during an account switch.
      if (scopedKey !== SEED_KEY && localGet(SEED_KEY) === v) {
        localRemove(SEED_KEY);
      }
      return v;
    }
  }

  const scoped = localGet(scopedKey);
  if (scoped) return scoped;

  // In a regular development browser there is no Telegram identity, so the
  // legacy key is the scoped key and was already checked above.
  if (scopedKey === SEED_KEY || !initData) return null;

  const legacy = localGet(SEED_KEY);
  if (!legacy) return null;
  try {
    if (!(await legacyBelongsToCurrentUser(initData, legacy, reauthToken))) return null;
    localSet(scopedKey, legacy);
    if (hasCloud()) await csSet(SEED_KEY, legacy).catch(() => {});
    localRemove(SEED_KEY);
    return legacy;
  } catch {
    // Offline, reauth required, wrong key, or wrong account: preserve the legacy
    // value for a later verified migration, but never expose it to this account.
    return null;
  }
}

async function writeStored(value: string): Promise<void> {
  localSet(localSeedKey(), value);
  if (hasCloud()) await csSet(SEED_KEY, value);
}

/** After /me resolves on web, move a pending seed slot onto the user-scoped key. */
export function adoptWebSeedKey(userId: string): void {
  const pending = `${SEED_KEY}:web:pending`;
  const scoped = `${SEED_KEY}:web:${userId}`;
  if (pending === scoped) return;
  const v = localGet(pending);
  if (v && !localGet(scoped)) {
    localSet(scoped, v);
    localRemove(pending);
  }
}

/** Upload the seed-encryption key to the server, retrying transient failures.
 *  Pass reauth when the server may need to rotate an orphan key. */
async function storeSeedKeyWithRetry(
  initData: string,
  keyB64: string,
  reauthToken?: string,
  attempts = 3,
): Promise<void> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      await storeSeedKey(initData, keyB64, reauthToken);
      return;
    } catch (e) {
      lastErr = e;
      // Immutable-key conflict (wallet already exists) is intentional, not transient.
      // Orphan rotate needs reauth — surface immediately.
      if (isSeedKeyExistsError(e) || String(e).includes('reauth_required') || String(e).includes('pin_required')) {
        throw e;
      }
      await new Promise((r) => setTimeout(r, 400 * (i + 1)));
    }
  }
  throw lastErr;
}

function isSeedKeyExistsError(e: unknown): boolean {
  const msg = String(e);
  return msg.includes('seed_key_exists') || /API 409\s+\/v1\/me\/seed-key/i.test(msg);
}

/** True when this mnemonic derives every registered address (ETH / TRX / BTC).
 *  BTC is only required when the server already has one — pre-BTC wallets are
 *  matched on ETH+TRX and backfilled after import. Extra derivation indices
 *  (multi-wallet) must all match too. */
export function seedMatchesWallet(mnemonic: string, remote: RemoteWallet): boolean {
  if (!validateMnemonic(mnemonic, wordlist)) return false;

  const byIndex = new Map<number, ReturnType<typeof deriveWallet>>();
  const at = (index: number) => {
    let w = byIndex.get(index);
    if (!w) {
      w = deriveWallet(mnemonic, index);
      byIndex.set(index, w);
    }
    return w;
  };

  let hasEth = false;
  let hasTrx = false;
  for (const a of remote.addresses) {
    const derived = at(a.derivationIndex ?? 0)[a.chain];
    if (!derived) continue; // a chain this build doesn't derive — don't fail on it
    // EVM addresses are checksummed, so case carries no meaning; Tron base58
    // and Bitcoin bech32 are case-significant and must match exactly.
    const matches = evmNetworkFor(a.chain)
      ? a.eoaAddress.toLowerCase() === derived.eoa.toLowerCase()
      : a.eoaAddress === derived.eoa;
    if (!matches) return false;
    if (a.chain === 'eth') hasEth = true;
    if (a.chain === 'trx') hasTrx = true;
  }
  // Primary ETH + TRX must exist on the server wallet.
  return hasEth && hasTrx;
}

/**
 * Restore an existing server wallet onto this device: reuse the immutable
 * server-held AES key (never POST a new one) and store ciphertext locally.
 * Throws `seed_mismatch` when the phrase does not match the registered addresses.
 */
export async function persistImportedSeed(
  initData: string,
  mnemonic: string,
  remote: RemoteWallet,
  reauthToken?: string,
): Promise<void> {
  if (!seedMatchesWallet(mnemonic, remote)) {
    throw new Error('seed_mismatch');
  }
  const { key } = await getSeedKey(initData, reauthToken);
  const ciphertext = await encryptSeed(mnemonic, key);
  await writeStored(ciphertext);
}

/** Encrypt a new seed and store ONLY the ciphertext locally + in CloudStorage,
 *  with the AES key handed to the server (its half of the split-key scheme).
 *
 *  Order matters: the key is stored on the server FIRST (with retries). Only once
 *  that succeeds do we write the ciphertext — otherwise a failed key upload would
 *  leave an undecryptable seed.
 *
 *  We NEVER fall back to storing the plaintext mnemonic: local/CloudStorage is
 *  readable on a shared/compromised device, and a plaintext seed there means full
 *  fund theft. If the key can't be secured we throw, and the caller must not treat
 *  the wallet as created (nothing decryptable is ever persisted). */
export async function persistNewSeed(
  initData: string,
  mnemonic: string,
  reauthToken?: string,
): Promise<void> {
  let keyB64 = await generateKeyB64();
  try {
    await storeSeedKeyWithRetry(initData, keyB64, reauthToken);
  } catch (e) {
    const msg = String(e);
    if (msg.includes('reauth_required') || msg.includes('pin_required')) throw e;
    if (!isSeedKeyExistsError(e)) throw e;
    // Wallet already has an immutable key (another device). Reuse it.
    const existing = await getSeedKey(initData, reauthToken);
    keyB64 = existing.key;
  }
  const ciphertext = await encryptSeed(mnemonic, keyB64);
  await writeStored(ciphertext);
}

export type SeedRead =
  | { ok: true; mnemonic: string }
  | { ok: false; reason: 'missing' | 'no_key' | 'decrypt' | 'reauth' | 'network' };

/** Resolve the plaintext mnemonic, with a reason when it cannot be opened.
 *  `no_key` / `decrypt` mean this device's copy is useless (typical after a
 *  reset deleted the server key). `reauth` / `network` are worth a retry. */
export async function readSeed(initData: string, reauthToken?: string): Promise<SeedRead> {
  const stored = await loadStoredSeed(initData, reauthToken).catch(() => null);
  if (!stored) {
    console.error('[getSeed] no stored seed on this device/session');
    return { ok: false, reason: 'missing' };
  }
  if (!isEncrypted(stored)) {
    const mnemonic = stored.trim();
    if (!validateMnemonic(mnemonic, wordlist)) return { ok: false, reason: 'decrypt' };
    await migrateLegacySeed(initData, reauthToken).catch(() => {});
    return { ok: true, mnemonic };
  }
  try {
    const { key } = await getSeedKey(initData, reauthToken);
    const mnemonic = (await decryptSeed(stored, key)).trim();
    if (!validateMnemonic(mnemonic, wordlist)) return { ok: false, reason: 'decrypt' };
    return { ok: true, mnemonic };
  } catch (e) {
    console.error('[getSeed] seed key fetch or decryption failed:', e);
    return { ok: false, reason: seedReadReason(e) };
  }
}

/** Resolve the plaintext mnemonic. Legacy plaintext is returned as-is; encrypted
 *  seeds need the server key (pass a reauth token when a PIN/biometric is set).
 *  Returns null if unavailable (e.g. reauth required, key missing). */
export async function getSeed(initData: string, reauthToken?: string): Promise<string | null> {
  const read = await readSeed(initData, reauthToken);
  return read.ok ? read.mnemonic : null;
}

/** Wipe the locally stored seed (localStorage + CloudStorage).
 *  Web sessions keep the ciphertext under a user id that is only known after
 *  /me. Reset used to delete the pending slot and leave the real one behind,
 *  so the next boot looked like a wallet it could not open. */
export async function clearLocalSeed(): Promise<void> {
  localRemove(localSeedKey());
  localRemove(`${SEED_KEY}:web:pending`);
  const web = getWebUserId();
  if (web) localRemove(`${SEED_KEY}:web:${web}`);
  if (hasCloud()) await csRemove(SEED_KEY).catch(() => {});
}

/** Encrypt a legacy plaintext seed in place (one-time migration on boot). */
export async function migrateLegacySeed(initData: string, reauthToken?: string): Promise<void> {
  const stored = await loadStoredSeed(initData, reauthToken);
  if (!stored || isEncrypted(stored)) return;
  try {
    // Prefer the immutable key already used by another device/ciphertext.
    const { key } = await getSeedKey(initData, reauthToken);
    await writeStored(await encryptSeed(stored, key));
  } catch (e) {
    // A genuinely keyless legacy user gets a new key. Reauth/network failures
    // are deliberately propagated so plaintext remains untouched for retry.
    if (!String(e).includes('no_key')) throw e;
    await persistNewSeed(initData, stored, reauthToken);
  }
}

/** The user's wallet from the backend, or null if not created yet. */
export async function fetchRemoteWallet(initData: string): Promise<RemoteWallet | null> {
  const r = await api<{ wallet: RemoteWallet | null }>(initData, '/v1/wallets');
  return r.wallet;
}

/** Generate a fresh 12-word BIP-39 mnemonic. */
export function createMnemonic(): string {
  return generateMnemonic(wordlist, 128);
}

/** One entry per supported chain — keyed by Record so a new chain cannot be
 *  left out of derivation and quietly never get an address. */
export type DerivedWallet = Record<Chain, { eoa: string; publicKey: string }>;

/** Derive an address per chain for the given account index (default 0).
 *  BTC uses BIP-84 (m/84'/0'/0'/0/i → bc1q); a coin needs no extra address, it
 *  lives on the same EOA as that chain's stablecoins. */
export function deriveWallet(mnemonic: string, index = 0): DerivedWallet {
  const seed = mnemonicToSeedSync(mnemonic);
  const evm = deriveEvmAddresses(seed, index);
  return {
    eth: evm,
    // Deliberately the same EOA, not a second account: BNB Smart Chain reuses
    // Ethereum's coin type, so a user's BSC address IS their Ethereum address.
    bsc: evm,
    sol: deriveSolanaAddresses(seed, index),
    // Same EOA as Ethereum again — HyperEVM reuses coin type 60.
    hyperevm: evm,
    trx: deriveTronAddresses(seed, index),
    btc: deriveBtcAddresses(seed, index),
    // Its own address space and its own scheme: a TON address is the hash of
    // the wallet CONTRACT's initial state, so it cannot share an EOA with
    // anything above.
    ton: deriveTonAddresses(seed, index),
    // Transparent only — we neither derive nor spend shielded addresses.
    zec: deriveZcashAddresses(seed, index),
    // Its own key schedule entirely: the BIP-32 node's private key is reduced
    // mod l to become the spend key, and everything after that is Monero's.
    xmr: deriveMoneroAddresses(seed, index),
    doge: deriveDogeAddresses(seed, index),
    xrp: deriveXrpAddresses(seed, index),
  };
}

/** Indices past the primary that a restore inspects. The server stores at most
 *  10 addresses per chain, and index 0 is registered with the wallet itself. */
const RESTORE_INDEX_MAX = 9;

/** After the primary wallet exists, register later indices that still hold funds.
 *  Monero is skipped: its balance cannot be read without a device-side scan. */
export async function restoreFundedAddresses(initData: string, mnemonic: string): Promise<void> {
  const seed = mnemonicToSeedSync(mnemonic);
  const addresses: Array<{
    chain: Chain;
    derivationIndex: number;
    eoaAddress: string;
    publicKey: string;
  }> = [];
  for (let index = 1; index <= RESTORE_INDEX_MAX; index++) {
    for (const chain of SUPPORTED_CHAINS) {
      if (chain === 'xmr') continue;
      const derived = deriveOne(seed, chain, index);
      addresses.push({
        chain,
        derivationIndex: index,
        eoaAddress: derived.eoa,
        publicKey: derived.publicKey,
      });
    }
  }
  await api<{ registered: Array<{ chain: Chain; derivationIndex: number }> }>(
    initData,
    '/v1/wallets/discover',
    { method: 'POST', body: JSON.stringify({ addresses }) },
  );
}

/** Register the derived addresses with the backend (the seed never leaves the device). */
export async function registerWallet(initData: string, w: DerivedWallet) {
  return api(initData, '/v1/wallets', {
    method: 'POST',
    body: JSON.stringify({
      name: 'Main wallet',
      seedBackupMethod: 'tg',
      // Built from the chain list rather than written out one by one, so a new
      // chain is registered for new wallets without touching this call.
      addresses: SUPPORTED_CHAINS.map((chain) => ({
        chain,
        eoaAddress: w[chain].eoa,
        publicKey: w[chain].publicKey,
        smartAccountAddress: null,
      })),
    }),
  });
}

/** One chain only. `deriveWallet` derives every chain in one call, so a throw
 *  in any of them (TON address encoding did this) registered none of them. */
function deriveOne(seed: Uint8Array, chain: Chain, index: number) {
  switch (chain) {
    case 'eth':
    case 'bsc':
    case 'hyperevm':
      return deriveEvmAddresses(seed, index);
    case 'sol':
      return deriveSolanaAddresses(seed, index);
    case 'trx':
      return deriveTronAddresses(seed, index);
    case 'btc':
      return deriveBtcAddresses(seed, index);
    case 'ton':
      return deriveTonAddresses(seed, index);
    case 'zec':
      return deriveZcashAddresses(seed, index);
    case 'xmr':
      return deriveMoneroAddresses(seed, index);
    case 'doge':
      return deriveDogeAddresses(seed, index);
    case 'xrp':
      return deriveXrpAddresses(seed, index);
  }
}

/** Derive a fresh address for one chain at `index` and register it (multi-wallet). */
export async function addWalletAddress(
  initData: string,
  mnemonic: string,
  chain: Chain,
  index: number,
  label?: string,
) {
  const a = deriveOne(mnemonicToSeedSync(mnemonic), chain, index);
  return api(initData, '/v1/wallets/addresses', {
    method: 'POST',
    body: JSON.stringify({ chain, eoaAddress: a.eoa, publicKey: a.publicKey, derivationIndex: index, label }),
  });
}

export async function renameWalletAddress(initData: string, addrId: string, label: string) {
  return api(initData, `/v1/wallets/addresses/${addrId}`, {
    method: 'PATCH',
    body: JSON.stringify({ label }),
  });
}

export async function deleteWalletAddress(initData: string, addrId: string, reauthToken?: string) {
  return api(initData, `/v1/wallets/addresses/${addrId}`, {
    method: 'DELETE',
    headers: reauthToken ? { 'X-Reauth': reauthToken } : undefined,
  });
}

export { validateMnemonic };
